import React from 'react'
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react'

export interface TooltipProps {
  label: React.ReactNode
  children: React.ReactElement
  placement?: 'top' | 'bottom' | 'left' | 'right'
  'data-testid'?: string
}

// A hover/focus micro-hint built on @floating-ui/react. Wraps a single
// trigger element (cloned with the merged ref + interaction props) and shows
// a small dark-surface panel with role="tooltip", linked to the trigger via
// aria-describedby. Opens on hover (after a short delay to avoid flicker) or
// keyboard focus, and closes on leave/blur/Escape.
const Tooltip = ({ label, children, placement = 'top', 'data-testid': testId }: TooltipProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const tooltipId = React.useId()

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  })

  const hover = useHover(context, { delay: { open: 150, close: 0 }, move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  // The trigger may already carry its own ref (e.g. a forwardRef component
  // passed in by a caller). Cloning with an explicit `ref` would clobber it,
  // so merge floating-ui's reference ref with whatever ref is already there.
  const childRef =
    (children.props as { ref?: React.Ref<unknown> }).ref ?? (children as { ref?: React.Ref<unknown> }).ref
  const mergedRef = useMergeRefs([refs.setReference, childRef])

  // `ref` must come after the `...children.props` spread: in React 19 an
  // element's own ref lives inside `children.props.ref`, so spreading props
  // after an explicit `ref` key would silently overwrite our merged ref with
  // the child's original (un-merged) one.
  const trigger = React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
    ...getReferenceProps({ ...(children.props as Record<string, unknown>), ref: mergedRef }),
    'aria-describedby': isOpen ? tooltipId : undefined,
  })

  return (
    <>
      {trigger}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="bg-ink text-white text-xs rounded-md px-2 py-1 shadow-md max-w-xs z-50"
            data-testid={testId}
            {...getFloatingProps()}
            id={tooltipId}
            role="tooltip"
          >
            {label}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

export default Tooltip
